import { get, isString } from "lodash-es";
import LMCMonthEnum from "../models/Custom/LMC/LMCMonthEnum";
import IOrder from "../models/IOrder";
import ISupplierModel from "../models/ISupplierModel";
import ISupplierSettings from "../models/ISupplierSettings";
import IThemeVariables from "../models/IThemeVariables";
import IUser from "../models/IUser";
import { v4 } from "uuid";
import SupplierService from "./SupplierService";
import UserService from "./UserService";
import ITokenSettings from "../models/ITokenSettings";
import IUserUI from "../models/IUserUI";
import { datadogRum } from "@datadog/browser-rum";
import UserTypesEnum from "../models/UserTypesEnum";
import MenuService from "./MenuService";
import { NavigateOptions, To } from "react-router-dom";
import * as qs from "qs";
import IConnection_Token from "../models/IConnection_Token";
import IVendorModel from "../models/IVendorModel";
import SocketService from "./SocketService";
import packageJson from "../../package.json";

const isMainViewLight = (elemId?: string): boolean => {
    let isLight = false;

    const elementId = elemId || "App";

    const headerElem = document.getElementById(elementId);

    if (headerElem) {
        const bgColor = window.getComputedStyle(headerElem, null).getPropertyValue("background-color");

        const colorMatch = bgColor.match(/(\d+),\s*(\d+),\s*(\d+)/);

        if (colorMatch) {
            const r = Number(colorMatch[1]);
            const g = Number(colorMatch[2]);
            const b = Number(colorMatch[3]);

            const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

            if (hsp > 127.5) {
                isLight = true;
            } else {
                isLight = false;
            }
        }
    }

    return isLight;
};

const getThemeType = (
    supplierModel: ISupplierModel,
    userModel: IUser,
    preview?: boolean
): { themeType: "DARK" | "LIGHT" | "CUSTOM_LIGHT" | "CUSTOM_DARK"; custom_light?: any; custom_dark?: any } => {
    let settings: ISupplierSettings;

    const currentToken = UserService.getCurrentToken(userModel as IUserUI);

    let tokenSettings: ITokenSettings | undefined;

    let custom_light: IThemeVariables | undefined;
    let custom_dark: IThemeVariables | undefined;

    if (!isString(supplierModel.settings)) {
        settings = supplierModel.settings;
    } else {
        settings = JSON.parse(supplierModel.settings);
    }

    if (currentToken?.settings) {
        if (!isString(currentToken.settings)) {
            tokenSettings = currentToken.settings;
        } else {
            tokenSettings = JSON.parse(currentToken.settings);
        }
    }

    let themeType: "DARK" | "LIGHT" | "CUSTOM_LIGHT" | "CUSTOM_DARK" = "DARK";

    if (tokenSettings?.theme) {
        themeType = tokenSettings.theme;
    } else {
        if (settings?.theme?.type) {
            themeType = settings.theme.type;
        }
    }

    if (!preview) {
        if (settings?.theme?.custom_light) {
            custom_light = settings.theme.custom_light;
        }

        if (settings?.theme?.custom_dark) {
            custom_dark = settings.theme.custom_dark;
        }
    } else {
        if (settings?.theme?.preview_light) {
            custom_light = settings.theme.preview_light;
        }

        if (settings?.theme?.preview_dark) {
            custom_dark = settings.theme.preview_dark;
        }
    }

    return { themeType, custom_light, custom_dark };
};
//
// const themePreview$ = (): BehaviorSubject<boolean> => {
//     return themePreviewSubject$;
// };
//
// const ecommerceView$ = (): BehaviorSubject<boolean> => {
//     return ecommerceViewSubject$;
// };
//
// const loadDashboard$ = (): BehaviorSubject<boolean> => {
//     return loadDashboardData$;
// };

const downloadCSVData = (csvData: any, filename: string) => {
    const blob = new Blob([csvData], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);

    link.download = filename;
    link.click();
};

/**
 * This resolves the issue where on a user focusing on an input or text area
 * that the Top Nav Bar disappears when it loses focus.
 */
const iOSKeyboardOpenFix = () => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
};

const convertMonthToNumber = (month: string): string => {
    let convertedMonth = "";

    switch (month) {
        case LMCMonthEnum.JANUARY:
            convertedMonth = "01";
            break;

        case LMCMonthEnum.FEBRUARY:
            convertedMonth = "02";
            break;

        case LMCMonthEnum.MARCH:
            convertedMonth = "03";
            break;

        case LMCMonthEnum.APRIL:
            convertedMonth = "04";
            break;

        case LMCMonthEnum.MAY:
            convertedMonth = "05";
            break;

        case LMCMonthEnum.JUNE:
            convertedMonth = "07";
            break;

        case LMCMonthEnum.JULY:
            convertedMonth = "07";
            break;

        case LMCMonthEnum.AUGUST:
            convertedMonth = "08";
            break;

        case LMCMonthEnum.SEPTEMBER:
            convertedMonth = "09";
            break;

        case LMCMonthEnum.OCTOBER:
            convertedMonth = "10";
            break;

        case LMCMonthEnum.NOVEMBER:
            convertedMonth = "11";
            break;

        case LMCMonthEnum.DECEMBER:
            convertedMonth = "12";
            break;

        default:
            convertedMonth = month.toString();
            break;
    }

    return convertedMonth;
};

const getMondaysInMonth = (year: string | number, month: string | number): number => {
    const convertedMonth = convertMonthToNumber(month.toString());

    // Ensure year and month are properly formatted
    const parsedYear = parseInt(year.toString(), 10);
    const parsedMonth = parseInt(convertedMonth.toString(), 10) - 1; // JavaScript months are 0-based (0-11)

    // Start with the first day of the month
    // const firstDayOfMonth = new Date(parsedYear, parsedMonth, 1);

    // Get the total number of days in the month
    const lastDayOfMonth = new Date(parsedYear, parsedMonth + 1, 0); // Day 0 of next month = last day of current month
    const daysInMonth = lastDayOfMonth.getDate();

    let mondayCount = 0;

    // Loop through each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(parsedYear, parsedMonth, day);
        // Check if the day is a Monday (1 = Monday in getDay(), where 0 = Sunday)
        if (currentDate.getDay() === 1) {
            mondayCount++;
        }
    }

    return mondayCount;
};

const getBusinessWeeksInMonth = (year: string | number, month: string | number): number => {
    const parsedYear = parseInt(year.toString(), 10);
    // Handle 1-based string input (e.g., "01" for January) and convert to 0-based
    const monthNum = parseInt(month.toString(), 10);
    const parsedMonth = monthNum - 1; // Adjust for 1-based input to 0-based

    // const firstDay = new Date(Date.UTC(parsedYear, parsedMonth, 1));
    // const lastDay = new Date(Date.UTC(parsedYear, parsedMonth + 1, 0));

    const firstSunday = new Date(Date.UTC(parsedYear, parsedMonth, 1));
    firstSunday.setUTCDate(firstSunday.getUTCDate() - firstSunday.getUTCDay());

    const lastSaturday = new Date(Date.UTC(parsedYear, parsedMonth + 1, 0));
    lastSaturday.setUTCDate(lastSaturday.getUTCDate() + (6 - lastSaturday.getUTCDay()));

    let weeksCount = 0;
    const currentWeekStart = new Date(firstSunday);

    while (currentWeekStart <= lastSaturday) {
        let hasBusinessDayInMonth = false;

        for (let i = 0; i < 7; i++) {
            const currentDay = new Date(currentWeekStart);
            currentDay.setUTCDate(currentDay.getUTCDate() + i);

            if (currentDay.getUTCMonth() === parsedMonth) {
                const dayOfWeek = currentDay.getUTCDay();
                if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                    hasBusinessDayInMonth = true;
                    break;
                }
            }
        }

        if (hasBusinessDayInMonth) {
            weeksCount++;
        }

        currentWeekStart.setUTCDate(currentWeekStart.getUTCDate() + 7);
    }

    return weeksCount;
};

const getWeeksInMonth = (year: string | number, month: string | number): number => {
    const parsedYear = parseInt(year.toString(), 10);
    const parsedMonth = parseInt(month.toString(), 10); // No subtraction, input is already 0-based

    const firstDay = new Date(Date.UTC(parsedYear, parsedMonth, 1));
    const lastDay = new Date(Date.UTC(parsedYear, parsedMonth + 1, 0));

    const firstDayOfWeek = firstDay.getUTCDay();
    const lastDayOfWeek = lastDay.getUTCDay();

    const daysInMonth = lastDay.getUTCDate();

    const daysBefore = firstDayOfWeek;
    const daysAfter = 6 - lastDayOfWeek;
    const totalDays = daysBefore + daysInMonth + daysAfter;

    const weeks = Math.ceil(totalDays / 7);

    return weeks;
};

/**
 * Get the number of weeks in a month
 *
 * @param year
 * @param month    0 based index
 */
const getIsoWeeksInMonth = (year: string | number, month: string | number): number => {
    // Parse year and month to numbers
    const parsedYear = parseInt(year.toString(), 10);
    const parsedMonth = parseInt(month.toString(), 10) - 1; // JavaScript months are 0-based (0-11)

    // Get the first and last day of the month
    const firstDay = new Date(parsedYear, parsedMonth, 1);
    const lastDay = new Date(parsedYear, parsedMonth + 1, 0); // Day 0 of next month = last day of current month

    // Array to store unique week numbers
    const weeks: number[] = [];

    // Iterate through each day of the month
    const currentDay = new Date(firstDay);
    while (currentDay <= lastDay) {
        // Get the week number for the current day
        const weekNumber = getWeekNumber(currentDay);

        // Add to weeks array if not already present
        if (!weeks.includes(weekNumber)) {
            weeks.push(weekNumber);
        }

        // Move to the next day
        currentDay.setDate(currentDay.getDate() + 1);
    }

    return weeks.length;
};

// Helper function to get the ISO week number of a date
const getWeekNumber = (date: Date): number => {
    const tempDate = new Date(date.getTime());
    tempDate.setHours(0, 0, 0, 0);
    // Thursday in current week decides the year
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));
    // January 4 is always in week 1
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from week 1
    return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const getViewOrderUrl = (row: IOrder, supplierModel: ISupplierModel): string => {
    let toUrl = SupplierService.updateStringWithOrderNameOverride(`/orders/${row.guid}/entry`, supplierModel);

    // if the order is a quote swap orders for quotes in the toUrl
    if (row.is_quote) {
        toUrl = toUrl.replace("/orders/", "/quotes/");
    } else if (row.is_template) {
        toUrl = toUrl.replace("/orders/", "/templates/");
    }

    if (supplierModel.has_ecommerce_view) {
        if (supplierModel.ecommerce_default_view) {
            toUrl = toUrl.replace("/entry", "/ecom");
        }
    }

    return toUrl;
};

const getLookupSize = (
    isMenuOpen: boolean,
    isMenuIconMode: boolean,
    isFixed?: boolean,
    setPadding?: number
): { lookupWidth: string; lookupLeft: string } => {
    let lookupWidth = "100%";
    let lookupLeft = "0px";

    if (isMenuIconMode) {
        // do nothing?
    }

    const mainView = document.getElementById("mainView");

    const padding = setPadding || 0;

    if (mainView && isMenuOpen) {
        lookupWidth = `${mainView.offsetWidth}px`;
        lookupLeft = `${mainView.offsetLeft + padding}px`;
    }

    if (isFixed && isMenuOpen) {
        const sideBar = document.getElementById("sideBar");

        if (sideBar) {
            lookupLeft = `${sideBar.offsetWidth + padding}px`;
        }
    }

    return { lookupWidth, lookupLeft };
};

const getLightThemeVariables = (): IThemeVariables => {
    return {
        mainBgColor: "#F6F4F3",
        secondaryBgColor: "#FFFFFF",
        minorBgColor: "#9DA9A0",
        centerViewBgColor: "#F6F4F3",
        mainTextColor: "#212121",
        highlightColor: "#333333",
        highlightColorText: "#FFFFFF",
        trackerHighlightColor: "#F89938",
        tabPanelBg: "#D7D5D9",
        tableOddRows: "#D7D5D9",
        tableEvenRows: "#FFFFFF",
        tableHighlightRow: "#D0CCD0",
        tableHoverRow: "#EDF6FF",
        overlayColor: "rgba(0,0,0,0.6)",
    } as IThemeVariables;
};

/**
 * Retrieves the css for displaying the Dock component
 * correctly based on the users theme selection.
 */
const getDockCss = (): { bg: string; color: string } => {
    let dockCss = {
        bg: "#313439",
        color: "white",
    };

    const mainViewLight = isMainViewLight();

    if (mainViewLight) {
        dockCss = {
            bg: "white",
            color: "",
        };
    }

    return dockCss;
};

/**
 * Download a file. Defaults type to text/csv
 *
 * @param data
 * @param filename
 * @param type
 */
const downloadDataFile = (data: any, filename: string, type = "text/csv") => {
    const blob = new Blob([data], { type });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);

    link.download = filename;
    link.click();
};

const stopScrollWheel = (e: any) => {
    // Prevent the input value change
    e.target.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current function is done)
    setTimeout(() => {
        e.target.focus();
    }, 0);
};

const getGuid = () => {
    return v4();
};

const roundNumber = (num: number, decimals = 2) => {
    let roundNum = 100;

    switch (decimals) {
        case 0:
            roundNum = 1;
            break;

        case 1:
            roundNum = 10;
            break;

        case 2:
            roundNum = 100;
            break;

        case 3:
            roundNum = 1000;
            break;

        case 4:
            roundNum = 10000;
            break;
    }
    return (Math.round(num * roundNum) / roundNum).toFixed(decimals);
};

const getOrderStateNameFromId = (stateId: number): string | undefined => {
    switch (stateId) {
        case 1:
            return "Preliminary";

        case 2:
            return "Submitted";

        case 3:
            return "In Review";

        case 4:
            return "Scheduled";

        case 5:
            return "In Production";

        case 6:
            return "Ready For Pickup";

        case 7:
            return "Delayed";

        case 8:
            return "Shipped";

        case 9:
            return "Completed";

        case 10:
            return "Vendor Approval Payment";

        case 11:
            return "Accounting";

        case 12:
            return "Payment Received Order Being Packed";

        case 13:
            return "Packing Completed Awaiting Shipping Info";

        case 14:
            return "Deposit Received Order Packing";

        case 15:
            return "Packing Completed Awaiting Payment Balance To Ship";

        case 16:
            return "Partially Shipped";

        case 17:
            return "Processed";

        case 18:
            return "Printed";

        case 19:
            return "Mounted";

        case 20:
            return "In Progress Packing";

        case 21:
            return "Ready To Ship";

        case 22:
            return "Delivered";

        case 23:
            return "Cancelled";

        case 24:
            return "Open";

        case 25:
            return "Closed";

        case 26:
            return "Laminated";

        case 27:
            return "In Queue";

        case 28:
            return "Awaiting Review";

        case 29:
            return "Under Review";

        case 30:
            return "Approved";

        case 31:
            return "Finishing";

        case 32:
            return "Picked Up";

        case 33:
            return "Submitted To Production";

        case 34:
            return "Weld Completed";

        case 35:
            return "Initial QC Completed";

        case 36:
            return "Paint Completed";

        case 37:
            return "Final QC Completed";

        case 38:
            return "Pending";

        case 39:
            return "Active";

        case 40:
            return "Ready For Delivery";

        case 41:
            return "In Design";

        case 42:
            return "Sent To Pharmacy";

        case 43:
            return "Awaiting Final Account Approval";

        case 44:
            return "Ready For Pick";

        case 45:
            return "Pre Press";

        case 46:
            return "Ready To Print";

        case 47:
            return "Printing";
    }
};

/**
 * Determine if the current location is the login path
 */
const isLoginPath = () => {
    if (location.href.indexOf("/login") >= 0) {
        return true;
    } else {
        return false;
    }
};

/**
 * Determine if the supplier model has a logo, and if yes, return the url based on the users theme selection
 *
 * @param supplierModel
 * @param userModel
 */
const getSupplierLogo = (supplierModel: ISupplierModel, userModel: IUserUI): { supplierLogo: boolean; supplierLogoUrl: string } => {
    let supplierLogo = false;
    let supplierLogoUrl = "";

    if (supplierModel && supplierModel.has_white_label && supplierModel.white_label_image_attachment_id) {
        supplierLogo = true;

        const { themeType } = UIService.getThemeType(supplierModel, userModel);

        if (themeType === "LIGHT") {
            supplierLogoUrl = get(supplierModel, "White_Label_Light_Image.signedUrl", "");
        } else {
            supplierLogoUrl = get(supplierModel, "White_Label_Image.signedUrl", "");
        }
    }

    return { supplierLogo, supplierLogoUrl };
};

/**
 * Determine if the current users UI Version is at the minimum allowed UI Version.
 *
 * True if the user is at the minimum version, false otherwise
 *
 * @param minUiVersion
 * @param pkgJsonVersion
 */
const isUserAtMinimumUiVersion = (minUiVersion: string, pkgJsonVersion: string) => {
    const splitMinUiVersionArr = minUiVersion.split(".");
    const splitPkgVersionArr = pkgJsonVersion.split(".");

    // Parse major versions
    const minUiVersionMajor = Number(splitMinUiVersionArr[0]);
    const pkgVersionMajor = Number(splitPkgVersionArr[0]);

    // Compare major versions first
    if (minUiVersionMajor > pkgVersionMajor) {
        return false;
    }
    if (minUiVersionMajor < pkgVersionMajor) {
        return true;
    }

    // Major versions are equal, compare minor versions
    const minUiVersionMinor = Number(splitMinUiVersionArr[1] || "0");
    const pkgVersionMinor = Number(splitPkgVersionArr[1] || "0");

    if (minUiVersionMinor > pkgVersionMinor) {
        return false;
    }
    if (minUiVersionMinor < pkgVersionMinor) {
        return true;
    }

    // Major and minor versions are equal, compare patch versions
    const minUiVersionPatch = Number(splitMinUiVersionArr[2] || "0");
    const pkgVersionPatch = Number(splitPkgVersionArr[2] || "0");

    return minUiVersionPatch <= pkgVersionPatch;
};

/**
 * Setup DataDog RUM
 *
 * @param userModel
 */
const setupDataDog = (userModel: IUserUI, environment: "production" | "development") => {
    const search = window.location.search;
    const splitSearch = search.split("?")[1];
    const urlParams = new URLSearchParams(splitSearch);

    let disableRum = (urlParams.get("disableRum") || "") === "true";

    if (disableRum) {
        // add this to the session storage to disable rum.
        sessionStorage.setItem("ow.disableRum", "true");
    } else {
        // get the value from the session storage
        disableRum = sessionStorage.getItem("ow.disableRum") === "true";
    }

    if (userModel.allow_rum && environment !== "development" && !disableRum) {
        datadogRum.init({
            applicationId: "5f0ca640-b83e-4716-8641-c36fe12d6602",
            clientToken: "pub0776c70a5ea32276122bd89dc5dd35c9",
            // `site` refers to the Datadog site parameter of your organization
            // see https://docs.datadoghq.com/getting_started/site/
            site: "datadoghq.com",
            service: "orderwerks-web-app",
            env: SocketService.getEnvironment(),
            version: packageJson.version || "",
            sessionSampleRate: 100,
            sessionReplaySampleRate: 100,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: "allow",
        });

        datadogRum.setUser({
            id: userModel.id.toString(),
            guid: userModel.guid,
            name: userModel.name,
            email: userModel.email,
            userType: userModel.userType?.name,
            supplierUser: userModel.isSupplierUser,
        });
    }
};

interface IInitialViewResult {
    path?: {
        to?: To;
        options?: NavigateOptions;
        pathname?: string;
        search?: string;
    };
    isEcommUser: boolean;
    isMinimumVersion: boolean;
    isOnboarding: boolean;
    isDashboardOrderEntry: boolean;
    performNavTo: boolean;
}

const determineInitialViewAndSetupMenu = (options: {
    minUiVersion: string;
    pkgJsonVersion: string;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    supplierUserToken: IConnection_Token | undefined;
    vendorUserToken: IConnection_Token | undefined;
    supplierVendorToken: IConnection_Token | undefined;
}): IInitialViewResult => {
    const result: IInitialViewResult = {
        isEcommUser: false,
        isMinimumVersion: true,
        isOnboarding: false,
        isDashboardOrderEntry: false,
        performNavTo: false,
    };

    // initial views are:

    // 1 - ui version check (outdated ui version)
    // 2 - login screen - no nav
    // 3 - unconnected account (no user tokens)
    // 4 - user onboarding (admin supplier user only atm)
    // 5 - if currently loaded user, and refresh, current route stays
    // 6 - user main landing
    // - ecomm ordering
    // - dashboard ordering
    // - dashboard
    // - navTo from url

    if (!UIService.isUserAtMinimumUiVersion(options.minUiVersion, options.pkgJsonVersion)) {
        result.isMinimumVersion = false;

        return result;
    }

    // const appConnectDetails = UserService.getLocalAppConnectDataFn();
    //
    // if (appConnectDetails) {
    //     setAppConnectDetails(appConnectDetails);
    //
    //     if (appConnectDetails.isShopify) {
    //         // getCatalogs();
    //     }
    // } else {
    /**
     * Perform the different checks to see where the user should be sent.
     */

    // Onboarding Users
    if (
        !options.supplierModel.has_onboarded &&
        options.userModel.isSupplierUser &&
        options.userModel.userType?.id === UserTypesEnum.ADMIN
    ) {
        result.path = {
            to: "/onboarding",
        };

        result.isOnboarding = true;

        return result;
    }

    const checkIfNavTo = () => {
        // Remove the leading '?' from window.location.search
        const parsedSearch = qs.parse(window.location.search.slice(1));

        const navTo = parsedSearch.navTo;

        return !!navTo;
    };

    // If Nav To In the URL
    if (checkIfNavTo()) {
        result.performNavTo = true;
        return result;
    }

    // If Dashboard Order Entry
    if (
        options.supplierUserToken?.dashboard_order_entry ||
        options.vendorUserToken?.dashboard_order_entry ||
        options.supplierVendorToken?.dashboard_order_entry
    ) {
        result.isDashboardOrderEntry = true;

        if (location.pathname === "/dashboard" || location.pathname === "/login" || location.pathname === "/accounting_change/loading") {
            result.path = {
                to: "/dashboard/ordering",
            };
        }

        return result;
    }

    // If Vendor && Ecommerce User
    if (options.vendorModel.is_ecomm_user) {
        result.isEcommUser = true;

        // if they're not in an ecomm view, send them to the home, otherwise keep as is.
        let onEcommRoute = false;

        if (location.pathname.indexOf("ecomm") >= 0) {
            onEcommRoute = true;
        }

        if (!onEcommRoute) {
            result.path = {
                pathname: "/ecomm/",
                search: location.search,
            };

            return result;
        }
    }

    // Check if vendor is an ecommerce user
    if (!options.userModel?.isSupplierUser && options.vendorModel?.is_ecomm_user) {
        result.isEcommUser = true;

        // The user may do a refresh on an ecomm item detail page.
        if (location.pathname !== "/ecomm" && location.pathname.indexOf("ecomm") === -1) {
            result.path = {
                to: "/ecomm",
            };
        }

        return result;
    }

    // Handle login redirection
    if (location.pathname === "/login" || location.pathname.indexOf("login") !== -1) {
        // For standard users, navigate to dashboard
        const dashboardPath = MenuService.getDashboardPath(options.userModel, options.vendorUserToken, options.supplierVendorToken);
        result.path = {
            to: dashboardPath || "/dashboard",
        };

        return result;
    }

    // Handle dashboard and accounting change loading paths
    if (location.pathname === "/dashboard" || location.pathname === "/accounting_change/loading") {
        const path = MenuService.getDashboardPath(options.userModel, options.vendorUserToken, options.supplierVendorToken);
        result.path = {
            to: path,
        };

        return result;
    }

    return result;
};

/**
 * Helper function to combine two class names together. Useful for conditionally adding classes.
 *
 * @param initialClassName
 * @param additionalClassName
 */
const combineClasses = (initialClassName: string, additionalClassName: string) => {
    return initialClassName + " " + additionalClassName;
};

const UIService = {
    // mainContentScrollObservable,
    // menuOpen$,
    // menuIconMode$,
    isMainViewLight,
    getThemeType,
    // themePreview$,
    // ecommerceView$,
    downloadCSVData,
    iOSKeyboardOpenFix,
    getWeeksInMonth,
    getIsoWeeksInMonth,
    getBusinessWeeksInMonth,
    getMondaysInMonth,
    getViewOrderUrl,
    // loadDashboard$,
    // offlineModeSubject$,
    // isLightMode$,
    // isSideBarOpen$,
    getLookupSize,
    getLightThemeVariables,
    // getCartSubject$,
    getDockCss,
    downloadDataFile,
    stopScrollWheel,
    getGuid,
    roundNumber,
    getOrderStateNameFromId,
    isLoginPath,
    getSupplierLogo,
    isUserAtMinimumUiVersion,
    setupDataDog,
    determineInitialViewAndSetupMenu,
    combineClasses,
};

export default UIService;
